// Shared formatting/labels for the LLM Usage & Token Consumption Analytics
// screens (Company Admin + Platform Admin). Kept out of the components so the
// same rules (cost honesty, bucket rendering, dimension columns) can't drift
// between the two surfaces.

import { PLATFORM_DIRECT_LABEL } from '../../../utils/tenancy';

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const nfCompact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

export function n0(v) {
  const n = Number(v);
  return Number.isFinite(n) ? nf0.format(n) : '—';
}

export function nCompact(v) {
  const n = Number(v);
  return Number.isFinite(n) ? nfCompact.format(n) : '—';
}

export function formatDurationMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(n < 10000 ? 2 : 1)} s`;
}

/** Browser tz — must always be sent to the API so a naive `start`/`end` isn't misread as UTC. */
export function getBrowserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Cost display honesty rule (spec §6): never show a total without its coverage.
 * `estimatedCost`/`costCoveragePct` null ⇒ no runs in range ⇒ "—", never "$0.00".
 */
export function formatCost(estimatedCost, costCoveragePct) {
  if (estimatedCost == null || costCoveragePct == null) {
    return { text: '—', tooltip: null };
  }
  const amount = `$${Number(estimatedCost).toFixed(2)}`;
  if (costCoveragePct >= 100) {
    return { text: amount, tooltip: null };
  }
  return {
    text: `${amount} · covers ${Math.round(costCoveragePct)}% of runs`,
    tooltip: 'Runs on models without a configured price are excluded from this estimate.',
  };
}

/**
 * `bucket` (spec §5) is a naive wall-clock string in `range.tz` with no `Z` —
 * parsed as plain string slices, never through a UTC-aware Date parser.
 */
export function formatBucket(bucket, grain = 'day') {
  if (!bucket) return '—';
  const [datePart, timePart = ''] = String(bucket).split('T');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return bucket;
  if (grain === 'month') return `${month}/${year}`;
  if (grain === 'hour') return `${month}/${day} ${timePart.slice(0, 5)}`;
  return `${month}/${day}/${year.slice(2)}`;
}

/**
 * Runs drill-down (spec §9 step 3) needs a start/end pair for a single bucket
 * (e.g. one day) when the breakdown is grouped by `date`. `Date.UTC` is used
 * purely as calendar-math scratch space here (not real UTC) so day/month
 * rollovers are handled correctly without depending on the browser's local tz
 * matching the `tz` param sent alongside these values.
 */
export function computeBucketRange(bucket, grain = 'day') {
  if (!bucket) return { start: undefined, end: undefined };
  const [datePart, timePart = '00:00:00'] = String(bucket).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh = 0, mm = 0, ss = 0] = timePart.split(':').map(Number);
  if (!y || !m || !d) return { start: undefined, end: undefined };

  const start = new Date(Date.UTC(y, m - 1, d, hh, mm, ss));
  const end = new Date(start);
  if (grain === 'hour') end.setUTCHours(end.getUTCHours() + 1);
  else if (grain === 'week') end.setUTCDate(end.getUTCDate() + 7);
  else if (grain === 'month') end.setUTCMonth(end.getUTCMonth() + 1);
  else end.setUTCDate(end.getUTCDate() + 1);

  const fmt = (dt) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}T${p(dt.getUTCHours())}:${p(dt.getUTCMinutes())}:${p(dt.getUTCSeconds())}`;
  };
  return { start: fmt(start), end: fmt(end) };
}

/**
 * `end` is EXCLUSIVE (spec §3): a UI date picker naturally means "through this
 * day, inclusive," so the picked end date is bumped by one day before it's
 * sent as the query param.
 */
export function toExclusiveEndDate(dateStr) {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

export const DIMENSION_LABELS = {
  user: 'User',
  company: 'Company',
  partner: 'Partner',
  model: 'Model',
  provider: 'Provider',
  agent: 'Agent',
  run_type: 'Run type',
  date: 'Date',
};

export const ALL_DIMENSIONS = ['user', 'company', 'partner', 'model', 'provider', 'agent', 'run_type', 'date'];

// row field → runs-endpoint query param. Names deliberately differ for
// provider (`llm_provider_id` on the row vs `provider_id` as a filter) — see
// spec §3 vs §4. A `partner` row with partner_id null is "Platform-direct" and
// has nothing to filter on, so the drill-down simply omits it.
export const DIMENSION_DRILLDOWN = {
  user: { rowField: 'user_id', param: 'user_id' },
  company: { rowField: 'company_id', param: 'company_id' },
  partner: { rowField: 'partner_id', param: 'partner_id' },
  model: { rowField: 'llm_model_id', param: 'llm_model_id' },
  provider: { rowField: 'llm_provider_id', param: 'provider_id' },
  agent: { rowField: 'agent_id', param: 'agent_id' },
  run_type: { rowField: 'run_type', param: 'run_type' },
};

export const RUN_TYPE_LABELS = {
  all: 'All types',
  single: 'Single',
  workflow_main: 'Workflow (main)',
  workflow_node: 'Workflow (node)',
  team: 'Team',
  batch_loop: 'Batch / loop',
  python_exec: 'Python step',
};

export const STATUS_LABELS = { all: 'All statuses', success: 'Success', error: 'Error' };

export const WINDOW_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '1d', label: 'Last 1 day' },
  { value: '2d', label: 'Last 2 days' },
  { value: '3d', label: 'Last 3 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '365d', label: 'Last 365 days' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
];

export const ORDER_BY_OPTIONS = [
  { value: 'total_tokens', label: 'Total tokens' },
  { value: 'input_tokens', label: 'Input tokens' },
  { value: 'output_tokens', label: 'Output tokens' },
  { value: 'calls', label: 'Calls' },
  { value: 'estimated_cost', label: 'Est. cost' },
  { value: 'errors', label: 'Errors' },
  { value: 'avg_duration_ms', label: 'Avg duration' },
  { value: 'tool_calls', label: 'Tool calls' },
];

export const TIMESERIES_METRICS = [
  { value: 'total_tokens', label: 'Tokens' },
  { value: 'calls', label: 'Calls' },
  { value: 'estimated_cost', label: 'Est. cost' },
];

/**
 * Render a breakdown row's value for one `group_by` dimension column.
 * `companyLabelFn(companyId, companyName)` lets a surface map a partner's home
 * company to "Direct users" (it must never show as a company).
 */
export function dimensionCellValue(dim, row, grain = 'day', companyLabelFn = null) {
  switch (dim) {
    case 'user':
      return row.user_name || row.user_email || (row.user_id != null ? `#${row.user_id}` : '—');
    case 'company':
      if (companyLabelFn && row.company_id != null) return companyLabelFn(row.company_id, row.company_name);
      return row.company_name || (row.company_id != null ? `#${row.company_id}` : '—');
    case 'partner':
      if (row.partner_id == null) return PLATFORM_DIRECT_LABEL;
      return row.partner_name || `#${row.partner_id}`;
    case 'model':
      return row.model_label || (row.llm_model_id != null ? `#${row.llm_model_id}` : '—');
    case 'provider':
      return row.provider || (row.llm_provider_id != null ? `#${row.llm_provider_id}` : '—');
    case 'agent':
      // Deleted agents keep their history: agent_id becomes null and agent_name
      // already carries "deleted agent" from the backend (spec §11).
      return row.agent_name || '—';
    case 'run_type':
      return RUN_TYPE_LABELS[row.run_type] || row.run_type || '—';
    case 'date':
      return formatBucket(row.bucket, grain);
    default:
      return '—';
  }
}
